import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';

function SettlementDetail() {
    const { brandID, month } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('statement');

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/brand/settlements/${brandID}/${month}`);
            if (response.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('Error fetching settlement detail:', error);
            navigate('/settlements');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
    }, [brandID, month]);

    if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading details...</div>;
    if (!data) return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Failed to load data</div>;

    const { period, statement, payments } = data;

    const getEventBadge = (event) => {
        const config = {
            order_placed: { bg: '#dbeafe', text: '#1e40af', label: 'Order' },
            order_delivered: { bg: '#e0e7ff', text: '#4338ca', label: 'Delivered' },
            return_window_cleared: { bg: '#d1fae5', text: '#065f46', label: 'Credit' },
            shipping_credit: { bg: '#ecfeff', text: '#155e75', label: 'Shipping' },
            returned: { bg: '#fee2e2', text: '#991b1b', label: 'Return' },
            replacement_original: { bg: '#fef3c7', text: '#92400e', label: 'Repl-Out' },
            replacement_item: { bg: '#f3f4f6', text: '#374151', label: 'Repl-In' }
        };
        const s = config[event] || { bg: '#f3f4f6', text: '#374151', label: event };
        return (
            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, backgroundColor: s.bg, color: s.text, textTransform: 'uppercase' }}>
                {s.label}
            </span>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                <button 
                    onClick={() => navigate('/settlements')}
                    style={{ background: 'transparent', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: 'var(--text)' }}
                >
                    ← Back
                </button>
                <div>
                    <h1 className="lux-heading" style={{ fontSize: '24px', marginBottom: '4px' }}>Statement: {month}</h1>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>Net Payable: <strong>₹{Number(period.netPayable).toLocaleString('en-IN')}</strong></p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{ background: 'var(--surface)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '8px' }}>Total Credit</div>
                    <div style={{ fontSize: '20px', fontWeight: 700 }}>₹{Number(period.totalCredits).toLocaleString('en-IN')}</div>
                </div>
                <div style={{ background: 'var(--surface)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '8px' }}>Total Debit</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#ef4444' }}>₹{Number(period.totalDebits).toLocaleString('en-IN')}</div>
                </div>
                <div style={{ background: '#065f46', padding: '20px', borderRadius: '12px', color: 'white' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, opacity: 0.8, textTransform: 'uppercase', marginBottom: '8px' }}>Net Amount</div>
                    <div style={{ fontSize: '24px', fontWeight: 800 }}>₹{Number(period.netPayable).toLocaleString('en-IN')}</div>
                </div>
                <div style={{ background: 'var(--surface)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '8px' }}>Amount Paid</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>₹{Number(period.amountPaid).toLocaleString('en-IN')}</div>
                </div>
            </div>

            <div style={{ background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
                    <button 
                        onClick={() => setActiveTab('statement')}
                        style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, background: activeTab === 'statement' ? 'rgba(0,0,0,0.05)' : 'transparent', border: 'none', color: activeTab === 'statement' ? 'var(--text)' : 'var(--muted)', cursor: 'pointer', borderBottom: activeTab === 'statement' ? '2px solid var(--text)' : 'none' }}
                    >
                        Statement Ledger
                    </button>
                    <button 
                        onClick={() => setActiveTab('payments')}
                        style={{ padding: '16px 24px', fontSize: '14px', fontWeight: 600, background: activeTab === 'payments' ? 'rgba(0,0,0,0.05)' : 'transparent', border: 'none', color: activeTab === 'payments' ? 'var(--text)' : 'var(--muted)', cursor: 'pointer', borderBottom: activeTab === 'payments' ? '2px solid var(--text)' : 'none' }}
                    >
                        Payment History
                    </button>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    {activeTab === 'statement' ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: 'rgba(0,0,0,0.02)', borderBottom: '1px solid var(--border)' }}>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Date</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Order ID</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>Event</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textAlign: 'right' }}>Amount</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Effect</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statement.map((row) => (
                                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '12px 16px', fontSize: '13px' }}>{new Date(row.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600 }}>#{row.orderID}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>{getEventBadge(row.event)}</td>
                                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700 }}>₹{Number(row.effectAmount || 0).toLocaleString('en-IN')}</td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: row.effect === 'credit' ? '#10b981' : row.effect === 'debit' ? '#ef4444' : '#f59e0b' }}>
                                                {row.effect}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{row.notes}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ background: 'rgba(0,0,0,0.02)', borderBottom: '1px solid var(--border)' }}>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Date</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textAlign: 'right' }}>Amount</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Mode</th>
                                    <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.length === 0 ? (
                                    <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>No payment history found.</td></tr>
                                ) : (
                                    payments.map((p) => (
                                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                            <td style={{ padding: '12px 16px', fontSize: '13px' }}>{new Date(p.paymentDate).toLocaleDateString('en-IN')}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: '#10b981' }}>₹{Number(p.amount).toLocaleString('en-IN')}</td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.paymentMode}</td>
                                            <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted)', fontMono: 'monospace' }}>{p.utrReference || 'N/A'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SettlementDetail;
